import { Link, useRouterState } from '@tanstack/react-router'
import {
  Building2,
  FileText,
  FileSearch,
  FolderTree,
  Keyboard,
  LayoutDashboard,
  Library,
  Menu,
  Plus,
  Upload,
  User,
  type LucideIcon,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { isFeatureEnabled } from '@/lib/feature-flags'
import { Button } from '@/components/ui/button'
import { BrandMark } from '@/components/ui/brand-mark'
import { Kbd } from '@/components/ui/kbd'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { useCurrentWorkspaceOrNull } from '@/api/workspace-context-provider'
import { canManageMembers } from '@/api/workspace-permissions'
import { useNavShortcuts } from '@/hooks/useNavShortcuts'
import { KeyboardShortcutsDialog } from './KeyboardShortcutsDialog'
import { WorkspaceSwitcher } from './WorkspaceSwitcher'
import { UserMenu } from './UserMenu'

type NavSection = 'workspace' | 'history' | 'account'

interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  /** Section label this item belongs to. */
  section: NavSection
  /** Optional one-letter shortcut hint shown in the sidebar (chord follows `g`). */
  shortcut?: string
  /**
   * When set, the item is only visible if `canShow(role)` is true. Items
   * without this gate are visible to all members. The gate runs against
   * the *current* workspace's role; when no workspace is loaded (initial
   * boot) gated items are hidden — coming back online refreshes the
   * sidebar so this is intentional, not a permanent hide.
   */
  requiresRole?: 'admin'
}

/**
 * Build the nav list, applying build-time feature-flag gating only to surfaces
 * that are still preview-only. Projects are a core workspace browser now:
 * backend project/folder/document-move flows already exist, so hiding the
 * route makes project context feel missing even when it is available.
 */
function buildNavItems(): readonly NavItem[] {
  const items: NavItem[] = [
    {
      to: '/dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      section: 'workspace',
      shortcut: 'D',
    },
    {
      to: '/projects',
      label: 'Projects',
      icon: FolderTree,
      section: 'workspace',
      shortcut: 'P',
    },
    {
      to: '/docfiller',
      label: 'Doc Filler',
      icon: FileText,
      section: 'workspace',
      shortcut: 'F',
    },
  ]
  items.push({
    to: '/prompts',
    label: 'Prompts',
    icon: Library,
    section: 'workspace',
    shortcut: 'R',
  })
  if (isFeatureEnabled('uploads_previews')) {
    items.push({
      to: '/uploads',
      label: 'Uploads',
      icon: Upload,
      section: 'workspace',
    })
  }
  items.push(
    {
      to: '/docfiller/chats',
      label: 'Search',
      icon: FileSearch,
      section: 'history',
      shortcut: 'S',
    },
    {
      to: '/workspaces',
      label: 'Workspaces',
      icon: Building2,
      section: 'account',
      shortcut: 'W',
    },
    {
      to: '/account',
      label: 'Account',
      icon: User,
      section: 'account',
    },
  )
  return items
}

const SECTION_LABELS: Record<NavSection, string> = {
  workspace: 'Workspace',
  history: 'History',
  account: 'Account',
}

const SECTION_ORDER: readonly NavSection[] = [
  'workspace',
  'history',
  'account',
] as const

interface NavListProps {
  wrapWithSheetClose?: boolean
  onNavigate?: () => void
}

function NavList({ wrapWithSheetClose = false, onNavigate }: NavListProps) {
  // Resolve the current workspace role so role-gated nav items can hide
  // for read-only members. `useCurrentWorkspaceOrNull` is the soft variant —
  // outside of WorkspaceProvider it returns null (so e.g. preview routes
  // that mount the shell directly don't crash). When null, gated items
  // are hidden, matching the conservative "hide when unsure" behavior of
  // the underlying ACL.
  const ctx = useCurrentWorkspaceOrNull()
  const role = ctx?.currentRole ?? null
  const navItems = buildNavItems()
  const visibleItems = navItems.filter((item) => {
    if (!item.requiresRole) return true
    if (item.requiresRole === 'admin') return canManageMembers(role)
    return false
  })

  const grouped = visibleItems.reduce<Partial<Record<NavSection, NavItem[]>>>(
    (acc, item) => {
      acc[item.section] = acc[item.section] ?? []
      acc[item.section]!.push(item)
      return acc
    },
    {},
  )

  return (
    <div className="flex flex-col gap-5">
      {SECTION_ORDER.map((section) => {
        const items = grouped[section]
        if (!items || items.length === 0) return null
        return (
          <div key={section} className="space-y-1">
            <div className="aurora-eyebrow px-3 pb-1.5">
              {SECTION_LABELS[section]}
            </div>
            <div className="flex flex-col gap-0.5">
              {items.map(({ to, label, icon: Icon, shortcut }) => {
                const link = (
                  <Link
                    key={to}
                    to={to}
                    onClick={onNavigate}
                    className={cn(
                      'group relative flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      'min-h-10 text-sidebar-foreground/85',
                      'hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground',
                      'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-sidebar-ring/30',
                    )}
                    activeProps={{
                      className:
                        'bg-sidebar-accent text-sidebar-accent-foreground shadow-soft',
                    }}
                  >
                    {/* Left accent bar — visible only when the link is active.
                        TanStack Router stamps `data-status="active"` on the
                        rendered anchor, so we can target it with a group
                        variant without depending on the active className. */}
                    <span
                      aria-hidden
                      className={cn(
                        'absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-sidebar-primary opacity-0 transition-opacity',
                        'group-data-[status=active]:opacity-100',
                      )}
                    />
                    <Icon className="size-4 shrink-0 opacity-80 group-hover:opacity-100" />
                    <span className="min-w-0 flex-1 truncate">{label}</span>
                    {shortcut ? (
                      <span
                        aria-hidden
                        className="hidden items-center gap-0.5 text-[10px] font-mono text-sidebar-foreground/45 group-hover:text-sidebar-foreground/70 md:flex"
                      >
                        <Kbd className="px-1 leading-none">G</Kbd>
                        <Kbd className="px-1 leading-none">{shortcut}</Kbd>
                      </span>
                    ) : null}
                  </Link>
                )
                if (!wrapWithSheetClose) return link
                return (
                  <SheetClose asChild key={to}>
                    {link}
                  </SheetClose>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/**
 * Primary action button surfaced in the topbar. Always points at the upload
 * surface — the canonical "new fill" entrypoint. Hidden on the doc-filler
 * upload route itself so we don't ship a button-to-self.
 */
function PrimaryAction() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  // Hide on the upload surface and the document detail viewer — both already
  // surface their own primary action (the dropzone itself, and the download
  // button respectively), so a second button would just be visual noise.
  const isUploadSurface =
    pathname === '/docfiller' ||
    (pathname.startsWith('/docfiller/') && pathname !== '/docfiller/chats')
  if (isUploadSurface) return null
  return (
    <Button asChild size="sm" className="gap-1.5">
      <Link to="/docfiller" data-testid="topbar-primary-action">
        <Plus className="size-4" />
        <span className="hidden sm:inline">New document</span>
        <span className="sr-only sm:hidden">New document</span>
      </Link>
    </Button>
  )
}

interface AppShellProps {
  children: ReactNode
}

/**
 * Aurora authenticated chrome.
 *
 * Desktop (≥ md):
 *   - 256px sidebar on the left with brand mark, workspace switcher, and a
 *     section-grouped nav rail (Workspace / History / Account). Active links
 *     get an accent bar + filled background. Sidebar hints display the
 *     keyboard chord beside each link (`G` then `D`, etc).
 *   - Top bar runs across the right pane with primary action button,
 *     shortcut help, and user menu at the trailing edge.
 *
 * Mobile:
 *   - Sticky top bar with a hamburger trigger that opens the same nav in a
 *     left-side Sheet. The aria-label includes "navigation" — the mobile-nav
 *     smoke test (`scripts/smoke-test-mobile-nav.ts`) keys off that string and
 *     the link role/name pair.
 *
 * Keyboard:
 *   - `useNavShortcuts` listens for the `g` chord (g+d, g+f, g+i, g+w, g+c)
 *     and the `?` help overlay. Inputs / Radix menus are excluded.
 */
export function AppShell({ children }: AppShellProps) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const isDocumentDetail = /^\/docfiller\/[^/]+$/.test(pathname)

  useNavShortcuts({ onShowHelp: () => setShortcutsOpen(true) })

  return (
    <div className="flex min-h-screen bg-background">
      {/* Skip-to-content target for keyboard / screen-reader users. The link
          is visually hidden until focused — first Tab from the page lands on
          it and lets you jump past the sidebar/topbar into <main>.
          Intentionally a plain anchor (not <Link>) because the destination is
          an in-page fragment, not a route. */}
      {/* eslint-disable-next-line no-restricted-syntax */}
      <a
        href="#main-content"
        data-testid="skip-to-content"
        className={cn(
          'sr-only',
          'focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-50',
          'focus:rounded-md focus:border focus:border-border focus:bg-background focus:px-3 focus:py-2',
          'focus:text-sm focus:font-medium focus:text-foreground focus:shadow-floating',
          'focus:outline-none focus:ring-[3px] focus:ring-ring/40',
        )}
      >
        Skip to main content
      </a>
      <aside
        className={cn(
          'hidden w-64 shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground md:flex',
          'sticky top-0 h-screen',
        )}
      >
        <div className="flex h-16 items-center px-5">
          <Link
            to="/dashboard"
            className="rounded-md focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-sidebar-ring/30"
          >
            <BrandMark size="md" />
          </Link>
        </div>
        <div className="aurora-divider mx-5" />
        <nav className="flex-1 overflow-y-auto py-4" aria-label="Primary">
          <div className="mb-3">
            <WorkspaceSwitcher />
          </div>
          <div className="px-3">
            <NavList />
          </div>
        </nav>
        <div className="border-t border-sidebar-border px-5 py-4">
          <button
            type="button"
            onClick={() => setShortcutsOpen(true)}
            className={cn(
              'flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-xs',
              'text-muted-foreground transition-colors',
              'hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground',
              'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-sidebar-ring/30',
            )}
            data-testid="sidebar-keyboard-shortcuts"
            aria-label="Show keyboard shortcuts"
          >
            <span className="flex items-center gap-1.5">
              <Keyboard className="size-3.5 opacity-70" aria-hidden />
              Shortcuts
            </span>
            <Kbd className="px-1">?</Kbd>
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header
          className={cn(
            'sticky top-0 z-30 flex items-center gap-2 border-b border-border/70 px-3 backdrop-blur',
            'bg-background/80 supports-[backdrop-filter]:bg-background/65',
            'sm:gap-3 sm:px-4 md:px-6',
            isDocumentDetail ? 'h-12 md:h-16' : 'h-16',
          )}
        >
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                aria-label="Open navigation menu"
              >
                <Menu className="size-5" />
                <span className="sr-only">Open navigation menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="w-72 border-r border-sidebar-border bg-sidebar p-0 sm:max-w-xs"
            >
              <SheetHeader className="px-5 pt-5 pb-3 text-left">
                <SheetTitle asChild>
                  <BrandMark size="md" />
                </SheetTitle>
              </SheetHeader>
              <div className="aurora-divider mx-5" />
              <nav
                className="flex-1 overflow-y-auto py-4"
                aria-label="Primary"
              >
                <div className="mb-3">
                  <WorkspaceSwitcher onSwitch={() => setMobileOpen(false)} />
                </div>
                <div className="px-3">
                  <NavList
                    wrapWithSheetClose
                    onNavigate={() => setMobileOpen(false)}
                  />
                </div>
              </nav>
            </SheetContent>
          </Sheet>

          {/* Mobile-only brand chip in the top bar; desktop hides it because
              the sidebar already shows the BrandMark. */}
          <div className="md:hidden">
            <BrandMark size="sm" wordmark={!isDocumentDetail} />
          </div>

          <div className="min-w-0 flex-1" />

          <div className="ml-auto flex items-center gap-1 sm:gap-2">
            <PrimaryAction />
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="hidden md:inline-flex"
              onClick={() => setShortcutsOpen(true)}
              aria-label="Show keyboard shortcuts"
              data-testid="topbar-keyboard-shortcuts"
            >
              <Keyboard className="size-4" />
              <span className="sr-only">Show keyboard shortcuts</span>
            </Button>
            <UserMenu onShowShortcuts={() => setShortcutsOpen(true)} />
          </div>
        </header>

        <main
          id="main-content"
          tabIndex={-1}
          className="flex-1 overflow-y-auto focus:outline-none"
        >
          {children}
        </main>
      </div>

      <KeyboardShortcutsDialog
        open={shortcutsOpen}
        onOpenChange={setShortcutsOpen}
      />
    </div>
  )
}
